import React, { memo as Memo, useMemo, useCallback, useRef, useState, useEffect } from 'react'

//* HOC's
import { withDataContext, withLanguageContext, withUIContext } from 'context'
import { withRouter } from 'next/router'

//* Components
import { Page, AnimationWrapp, Container, Text, Image, Preloader } from 'components/common'
import { FilterInCatalogue, MapInCatalogue, Catalogs } from 'components/pages'

//* Helpers
import { routerToPathAndQuery, arrayGenChild, arrayGen } from 'helpers'

//* Styles
import InvestmentProjectsStyle from 'styles/pages/InvestmentProjectsStyle'

const InvestmentProjects = Memo(({ pages, projects, global, pageParams, selectedLang, router, winWidth, loading, preloader, ...props }) => {
    //! Page Data
    const pageData = useMemo(() => pages?.[pageParams.name]?.[selectedLang], [pages, selectedLang, pageParams.name])
    const projectsData = useMemo(() => projects?.[pageParams?.queryName]?.[selectedLang], [projects, selectedLang, pageParams.queryName])
    const globalData = useMemo(() => global?.[selectedLang], [global, selectedLang])

    //! States
    const [secObj, setSecObj] = useState()

    //! Refs
    const TitleRef = useRef()
    const Ref1 = useRef()
    const Ref2 = useRef()
    const Ref3 = useRef()

    //! Filter Change
    const onCategoryChange = useCallback(
        (cat, catId, multiple = true) => {
            const { pathname, query } = routerToPathAndQuery(router, cat, catId, multiple)

            multiple && delete query[pageParams.postType.queryParamKeys[2]] //? Page Param Removed on Category Change
            router.push({ pathname, query }, undefined, { scroll: false })
        },
        [router.query]
    )

    //! Animation
    useEffect(() => {
        if (pageData && projectsData && TitleRef?.current && Ref1?.current && Ref2?.current?.[1]?.current?.childNodes?.[4] && Ref3?.current && !preloader) {
            winWidth > 1023
                ? setSecObj([
                    arrayGenChild(TitleRef.current.childNodes[0].childNodes),
                    Ref1.current[0].current.childNodes,
                    Ref2.current[0].current,
                    arrayGen([
                        [Ref2.current[1].current.childNodes[0].childNodes[0]],
                        Ref2.current[1].current.childNodes[0].childNodes[1].childNodes[0].childNodes[0].childNodes[0].childNodes,
                    ]),
                    arrayGen([
                        [Ref2.current[1].current.childNodes[1].childNodes[0]],
                        Ref2.current[1].current.childNodes[1].childNodes[1].childNodes[0].childNodes[0].childNodes[0].childNodes,
                    ]),
                    arrayGen([
                        [Ref2.current[1].current.childNodes[2].childNodes[0]],
                        Ref2.current[1].current.childNodes[2].childNodes[1].childNodes[0].childNodes[0].childNodes[0].childNodes,
                    ]),
                    arrayGen([
                        [Ref2.current[1].current.childNodes[3].childNodes[0]],
                        Ref2.current[1].current.childNodes[3].childNodes[1].childNodes[0].childNodes[0].childNodes,
                    ]),
                    arrayGen([
                        [Ref2.current[1].current.childNodes[4].childNodes[0]],
                        Ref2.current[1].current.childNodes[4].childNodes[1].childNodes[0].childNodes[0].childNodes,
                    ]),
                    Ref2.current[2]?.current,
                    Ref3.current[0]?.current.childNodes,
                    Ref3.current[1]?.current?.childNodes,
                ])
                : setSecObj([
                    arrayGenChild(TitleRef.current.childNodes[0].childNodes),
                    Ref1.current[0].current.childNodes,
                    Ref3.current?.[1]?.current.childNodes,
                ])
        }
    }, [pageData, projectsData, TitleRef.current, Ref1.current, Ref2.current, Ref3.current, router.asPath, winWidth, loading, preloader])

    const catalogue = useMemo(() => {
        if (loading) {
            return (
                <div className='relativeWrapp'>
                    <Preloader className={'preloaderProductProject'} />
                </div>
            )
        } else {
            return (
                <Catalogs
                    onPageChange={onCategoryChange}
                    sorts={pageData?.filters.sorts}
                    catalogsArr={projectsData?.projects}
                    total={projectsData?.total}
                    itemsCountPerPage={projectsData?.perPageCount}
                    queryParamKeys={pageParams.postType.queryParamKeys}
                    ref={Ref3}
                    projects={true}
                >
                    <div className='no-result' ref={Ref3}>
                        <Image src='/images/noResult.svg' alt={props.translate('noResult')} />
                        <Text tag={'h5'} className={'title h5 MonRegular black'} text={'noResult'} />
                        <Text tag={'p'} className={'pM MonRegular ultraDarkGrey'} text={'noResultText'} />
                    </div>
                </Catalogs>
            )
        }
    }, [projectsData, loading])

    return (
        <InvestmentProjectsStyle>
            <Page
                className='investment-projects'
                meta={{
                    title: pageData?.content?.meta?.title,
                    description: pageData?.content?.meta.description,
                    keywords: pageData?.content?.meta.keywords,
                    image: pageData?.content.meta_image,
                }}
            >
                {pageData && (
                    <AnimationWrapp state={secObj}>
                        <Container className='inv-proj'>
                            <Text
                                tag={'stringToLetters'}
                                className={'title page-title h2 MonBold black'}
                                text={pageData.content.title}
                                ref={TitleRef}
                            />

                            <MapInCatalogue projects={projectsData?.projects} ref={Ref1} />

                            <div className={`main-block ${loading ? 'disable' : ''}`}>
                                <FilterInCatalogue
                                    onCategoryChange={onCategoryChange}
                                    categories={pageData.filters.categories}
                                    states={pageData.filters.locations}
                                    ranges={globalData?.project_prices}
                                    queryParamKeys={pageParams.postType.queryParamKeys}
                                    ref={Ref2}
                                />
                                {catalogue}
                            </div>
                        </Container>
                    </AnimationWrapp>
                )}
            </Page>
        </InvestmentProjectsStyle>
    )
})

export default withRouter(
    withUIContext(withLanguageContext(withDataContext(InvestmentProjects, ['pages', 'global', 'projects']), ['selectedLang', 'translate']), [
        'winWidth',
        'loading',
    ])
)
